import { useEffect, useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import { API, fmtErr, money } from "../lib/pos";

export function CategoriesAdmin({ stations, refresh }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // {id, name}
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({}); // catId -> bool (items dropdown)

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/categories/manage`);
      setCats(r.data);
    } catch (e) { alert(fmtErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addCat = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await axios.post(`${API}/categories`, { name });
      setNewName("");
      await load();
      refresh && refresh();
    } catch (e) { alert(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const renameCat = async () => {
    if (!editing) return;
    const name = (editing.name || "").trim();
    if (!name) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/categories/${editing.id}`, { name });
      setEditing(null);
      await load();
      refresh && refresh();
    } catch (e) { alert(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const removeCat = async (c) => {
    if (c.item_count > 0) return;
    if (!window.confirm(`Delete category "${c.name}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/categories/${c.id}`);
      await load();
      refresh && refresh();
    } catch (e) { alert(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const changeStation = async (c, newStationId) => {
    try {
      const currentId = c.station_id || null;
      if (currentId === newStationId) return;
      // Confirmation: switching a category's station reroutes every future KOT for its items.
      const currentName = currentId ? (stations.find((s) => s.id === currentId)?.name || "current station") : "Unassigned";
      const nextName = newStationId ? (stations.find((s) => s.id === newStationId)?.name || "the new station") : "Unassigned";
      const itemNote = c.item_count > 0 ? ` This will affect ${c.item_count} item${c.item_count > 1 ? "s" : ""} in this category.` : "";
      if (!window.confirm(`Change the station for "${c.name}" from ${currentName} → ${nextName}?${itemNote}\n\nEvery future KOT for these items will be routed to ${nextName}.`)) {
        return;
      }
      if (newStationId) {
        // POST enforces single-station (backend strips from others)
        await axios.post(`${API}/categories/${c.id}/stations/${newStationId}`);
      } else if (currentId) {
        // "Unassigned" chosen — remove from current station
        await axios.delete(`${API}/categories/${c.id}/stations/${currentId}`);
      }
      await load();
      refresh && refresh();
    } catch (e) { alert(fmtErr(e)); }
  };
  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/categories/${c.id}/active`, { active: !c.active });
      await load();
      refresh && refresh();
    } catch (e) { alert(fmtErr(e)); }
  };

  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">MANAGER TOOLS / CATEGORIES</p>
          <h1>Categories</h1>
          <p className="muted">Create, rename, disable, delete and route each category to exactly one kitchen station.</p>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="section-line"><h2>Add category</h2><span className="muted">Empty categories are allowed; add items to them later in Menu Builder.</span></div>
        <div className="cat-add-row" style={{ marginTop: 10 }}>
          <input
            data-testid="new-category-input"
            type="text"
            placeholder="e.g. Chef's Specials"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addCat(); }}
            maxLength={60}
          />
          <button data-testid="add-category-button" className="primary" disabled={!newName.trim() || busy} onClick={addCat}>
            <Plus size={14} />Add
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="section-line">
          <h2>All categories</h2>
          <span className="muted" data-testid="categories-count">{cats.length} total · {cats.filter((c) => c.item_count > 0).length} in use · {cats.filter((c) => c.active === false).length} disabled</span>
        </div>
        {loading ? (
          <p className="muted" style={{ marginTop: 12, fontSize: 12 }}>Loading…</p>
        ) : cats.length === 0 ? (
          <p className="muted" style={{ marginTop: 12, fontSize: 12 }}>No categories yet — add your first one above.</p>
        ) : (
          <div className="cat-table" data-testid="categories-table" style={{ marginTop: 12 }}>
            {cats.map((c) => {
              const isDisabled = c.active === false;
              const isExpanded = !!expanded[c.id];
              const station = stations.find((s) => s.id === c.station_id) || null;
              return (
                <div className={`cat-row ${isDisabled ? "cat-off" : ""}`} key={c.id} data-testid={`category-row-${c.id}`}>
                  <div className="cat-main">
                    <button
                      className="cat-expand"
                      data-testid={`category-${c.id}-expand-button`}
                      onClick={() => setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))}
                      title={isExpanded ? "Hide items" : "Show items"}
                    >{isExpanded ? "▼" : "▶"}</button>
                    <div>
                      <b>{c.name}{isDisabled && <small className="off-tag"> DISABLED</small>}</b>
                      <small>{c.item_count} item{c.item_count === 1 ? "" : "s"}{station && ` · ${station.name}`}</small>
                    </div>
                  </div>
                  <div className="cat-stations">
                    <label className="field cat-station-select" style={{ margin: 0 }}>
                      <select
                        data-testid={`category-${c.id}-station-select`}
                        value={c.station_id || ""}
                        onChange={(e) => changeStation(c, e.target.value || null)}
                        disabled={busy}
                      >
                        <option value="">Unassigned</option>
                        {stations.map((s) => {
                          const isExpo = !(s.assigned_categories || []).length && s.id !== c.station_id;
                          return (
                            <option key={s.id} value={s.id} disabled={isExpo}>
                              {s.name}{isExpo ? " · sees all" : ""}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  </div>
                  <div className="cat-actions">
                    <button
                      data-testid={`category-${c.id}-active-toggle`}
                      className={`toggle ${isDisabled ? "off" : "on"}`}
                      onClick={() => toggleActive(c)}
                      title={isDisabled ? "Enable — items become orderable" : "Disable — hides items in POS"}
                    >
                      <i /><span>{isDisabled ? "OFF" : "ON"}</span>
                    </button>
                    <button data-testid={`edit-category-${c.id}-button`} className="outline" onClick={() => setEditing({ id: c.id, name: c.name })}>Edit</button>
                    <button
                      data-testid={`delete-category-${c.id}-button`}
                      className={`outline ${c.item_count > 0 ? "disabled" : "danger"}`}
                      disabled={c.item_count > 0 || busy}
                      title={c.item_count > 0 ? `Reassign the ${c.item_count} item(s) first` : ""}
                      onClick={() => removeCat(c)}
                    >Delete</button>
                  </div>
                  {isExpanded && (
                    <div className="cat-items-list" data-testid={`category-${c.id}-items`}>
                      {(c.items || []).length === 0 ? (
                        <p className="muted" style={{ fontSize: 12, margin: "8px 0 0" }}>No items in this category yet.</p>
                      ) : (
                        <ul>
                          {c.items.map((it) => (
                            <li key={it.id} data-testid={`category-${c.id}-item-${it.id}`}>
                              <b>{it.name}</b>
                              <span>{money(it.price)}</span>
                              {it.active === false && <em className="off-tag">disabled</em>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setEditing(null)}>
          <div className="modal" style={{ width: "min(440px, 92vw)" }} data-testid="rename-category-modal">
            <div className="section-line">
              <h2>Rename category</h2>
              <button data-testid="close-rename-modal-button" onClick={() => setEditing(null)}>×</button>
            </div>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
              Renaming cascades to all menu items, station assignments and open orders.
            </p>
            <label className="field">Name
              <input
                data-testid="rename-category-input"
                type="text"
                value={editing.name}
                maxLength={60}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                autoFocus
              />
            </label>
            <button data-testid="save-category-button" className="primary wide" disabled={!editing.name.trim() || busy} onClick={renameCat}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Station Admin (manager) ---------- */
